import React from 'react';
import {KeyboardModule, ModuleInitPhase, ScreenParameters} from '@gongxigames/framework/core';
import {CoreSprite, FullscreenCover} from '@gongxigames/framework/core/fragments/react';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {Slides} from '@gongxigames/framework/slot/modules/loadingScreen/components/Slides';
import {ILoadingScreenView} from '@gongxigames/framework/slot/modules/loadingScreen/ILoadingScreenView';
import {LoadingScreenModule} from '@gongxigames/framework/slot/modules/loadingScreen/LoadingScreenModule';
import {LoaderContainer} from '@gongxigames/framework/slot/modules/loadingScreen/components/LoaderContainer';
import {ProgressIndicator} from '@gongxigames/framework/slot/modules/loadingScreen/components/ProgressIndicator';
import {ButtonStart} from '@gongxigames/framework/slot/modules/loadingScreen/components/ButtonStart';
import {ReactView} from '../../fragments/module/view';

type LoadingScreenViewState = {
    started: boolean;
    loadingComplete: boolean;
    shouldShowStartButton: boolean;
    progress: number;
    screenParameters: ScreenParameters | undefined;
    hide?: boolean;
};

export class LoadingScreenViewReact extends ReactView<LoadingScreenViewState> implements ILoadingScreenView {
    public static NAME = LoadingScreenModule.VIEW;
    public static initPhase: ModuleInitPhase = 'initial';
    public static layer = LAYERS.TOP;

    public state: LoadingScreenViewState = {
        started: false,
        loadingComplete: false,
        shouldShowStartButton: true,
        progress: 0,
        screenParameters: undefined
    };

    public start(): void {
        this.setState({started: true});
    }

    protected setupEvents(): void {
        const KEYS = [KeyboardModule.ENTER_CODE, KeyboardModule.SPACEBAR_CODE];

        super.setupEvents();

        const listener = (keyCode: number): void => {
            if (KEYS.includes(keyCode)) {
                this.onClick();
            }
        };

        this.eventManager.on('General.UI.KEY_UP', listener, this);
    }

    protected removeEvents(): void {
        super.removeEvents();
        this.eventManager.off('General.UI.KEY_UP', this);
    }

    public loadingComplete(hideImmediately = false): void {
        this.setState({loadingComplete: true, progress: 1, hide: hideImmediately});
    }

    public enableFirstInteraction(enable = false): void {
        this.setState({shouldShowStartButton: enable});
    }

    public updateProgress(progress: number): void {
        this.setState({progress});
    }

    public onResized(screenParameters: ScreenParameters): void {
        this.setState({screenParameters});
    }

    protected onClick(): void {
        if (!this.state.loadingComplete) {
            return;
        }

        this.emit('LoadingScreen.START_BUTTON_PRESSED');
        this.setState({hide: true});
        this.remove();
    }

    /**
     * For override
     */
    public renderBackground(): JSX.Element {
        return <FullscreenCover screenParameters={this.state.screenParameters!} interactive={true}>
            <CoreSprite src='background_preloader'/>
        </FullscreenCover>;
    }

    /**
     * For override
     */
    public renderElements(): JSX.Element {
        return <>
            <CoreSprite anchor={0.5} src='logo' position={[0, -900]}/>
            <Slides position={[0, -80]} updateManager={this._updateManager} dotsPosition={[0, -120]}>
                <Slides.Slide time={8000}><CoreSprite anchor={0.5} src='logo'/></Slides.Slide>
            </Slides>
        </>;
    }

    public render(): JSX.Element {
        const {started, loadingComplete, progress, screenParameters, hide, shouldShowStartButton} = this.state;
        const platform = this._settings.get('platform');

        if (!started || !screenParameters || hide) {
            return <></>;
        }

        return <>
            {this.renderBackground()}
            <LoaderContainer screenParameters={screenParameters} platform={platform}>
                {this.renderElements()}
                <ProgressIndicator progress={progress} visible={!loadingComplete}/>
                <ButtonStart press={this.onClick.bind(this)} visible={loadingComplete && shouldShowStartButton}/>
            </LoaderContainer>
        </>;
    }
}
